import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { createSearchParams, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../providers/AuthContextProvider";
import { StyleContext } from "../providers/StyleContextProvider";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { Button } from "../components/Shared/Buttons/Button";
import { ReactComponent as LeftArrow } from "../assets/svg/left_arrow.svg";
import { getPromoteAdDetails } from "../services/RecruiterService";
import { LongJobCard } from "../components/Shared/LongJobCard";
import useErrorPage from "../hooks/useErrorPage";
import { useMediaQuery } from "react-responsive";
import { CheckboxInput } from "../components/Shared/Inputs/CheckboxInput";
import { ReactComponent as BumpUp } from "../assets/svg/bump-up.svg";
import { Tag } from "../components/Shared/Tag";
import { Footer } from "../components/Shared/Footer";
import { PaymentIcons } from "../helpers/LayoutTexts";
import $ from 'jquery';
import { TextInput } from "../components/Shared/Inputs/TextInput";
import { getPromoCodeValidation } from "../services/RecruiterService";
import { PromoCode } from "../models/PromoCode";
import { CheckoutTypes } from "../enums/CheckoutTypes";
import { WebXPayModel } from "../models/WebXPayModel";
import { postCheckoutRequest } from "../services/CheckoutService";
import { CheckoutModel, PaymentDetailsValidation } from "../models/CheckoutModel";
import { PromoteAdLoader } from "../components/Loaders/PromoteAdLoader";
import { ActivePromotionsLoader } from "../components/Loaders/ActivePromotionsLoader";
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { Job } from "../models/Job";
import { AdPromotionType } from "../models/AdPromotionType";
import { UrgentJob } from "../models/UrgentJob";
import { validateAddress, validateTelephoneNumber } from "../helpers/Common";

type PaymentModel = {    
    job: Job;
    adPromotionTypes: AdPromotionType[];
    activeBumpUps: number;
    activeUrgentJobs: UrgentJob;
    checkoutTypeId: number;
    organizationTelephone: string;
    organizationTINNumber: string;
    organizationAddress: string;
}

const intialFormInputs: CheckoutModel = {     
    fullName: "",
    email: "",
    promoCode: ""
}

const paymentDetailsValidation: PaymentDetailsValidation = {
    telephone: {},
    tinNumber: {},
    billingAddress: {}
}

export const PromoteAdPage: React.FC = () => {
    let params = useParams();
    const jobId = params.jobId;
    const location = useLocation();
    const navigate = useNavigate();    
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("promoteAdPage"));  
    const [promoteAdData, setPromoteAdData] = useState<PaymentModel>();
    const [isBumpUpSelected, setIsBumpUpSelected] = useState<boolean>(false);
    const [isUrgentSelected, setIsUrgentSelected] = useState<boolean>(false);
    const [selectedBumpUp, setSelectedBumpUp] = useState<AdPromotionType>();
    const [selectedUrgent, setSelectedUrgent] = useState<AdPromotionType>();    
    const [price, setPrice] = useState<number>(0);
    const [packagePrice, setPackagePrice] = useState<number>(0);
    const [discountRate, setDiscountRate] = useState<number>();
    const [discount, setDiscount] = useState<number>();    
    const [vatAmount, setVatAmount] = useState<number>(0);  
    const [promoCode, setPromoCode] = useState<string>("");
    const [promoInvalid, setPromoInvalid] = useState<boolean>(false);
    const [promoInvalidMessage, setPromoInvalidMessage] = useState<string>();
    const [checkout, setCheckout] = useState<CheckoutModel>(intialFormInputs);
    const [loading, setLoading] = useState<boolean>(true);
    const [validation, setValidation] = useState<PaymentDetailsValidation>(paymentDetailsValidation);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)"}); 
    const [toErrorPage] = useErrorPage();

    useEffect(() => {        
        if(recruiter === null) {   
            const params = { callbackUrl: location.pathname + location.search, message: "Please login or register to access the ad promotion." };
            navigate({
                pathname: '/recruiter/account/login',
                search: `?${createSearchParams(params)}`
            });            
        }   
        else {
            getPromoteAdData();
            let tempFormInputs: CheckoutModel = {  
                recruiterId: recruiter.recruiterId,   
                fullName: recruiter.forename + " " + recruiter.surname,
                email: recruiter.email,
                promoCode: "",
            }
            setCheckout(tempFormInputs);
        } 
    }, []);

    useLayoutEffect(() => {
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'manual';
            }
            window.scrollTo(0, 0);
            const handlePageShow = (event: PageTransitionEvent) => {
                if (event.persisted) {
                    window.location.reload();
                }
            };
            window.addEventListener("pageshow", handlePageShow);
            return () => {
                window.removeEventListener("pageshow", handlePageShow);
                if ('scrollRestoration' in window.history) {
                    window.history.scrollRestoration = 'auto';
                }
            };
    }, []);

    useEffect(() => {
        if(!isBumpUpSelected && !isUrgentSelected) {   
            setDiscount(undefined);
            setDiscountRate(undefined);
            setPromoCode("");
            setPromoInvalid(false);
            setPromoInvalidMessage(undefined);
        } 
    }, [isBumpUpSelected, isUrgentSelected]);

    useEffect(() => {
        let total = 0;
        if (selectedBumpUp) {
            total += selectedBumpUp.value;
        }
        if (selectedUrgent) {
            total += selectedUrgent.value;
        }
        setPackagePrice(total);
        if (discountRate) {
            let deduction = (total * discountRate) / 100;
            setDiscount(deduction);
            total -= deduction;
        }
        let vatAmount = total * (Number(process.env.REACT_APP_VAT_RATE) / 100);
        setVatAmount(vatAmount);
        setPrice(total + vatAmount);
    }, [selectedBumpUp, selectedUrgent, discountRate]);

    const handleBumpUpSelection = () => {
        if (isBumpUpSelected) {
            setSelectedBumpUp(undefined);
            setPromoteAdData((prev) => ({
                ...prev,
                job: {
                    ...prev?.job,
                    bumpUp: false,
                } as Job,
            } as PaymentModel));
        }
        setIsBumpUpSelected(!isBumpUpSelected);        
    };

    const handleUrgentSelection = () => {
        if (isUrgentSelected) {
            setSelectedUrgent(undefined);
            setPromoteAdData((prev) => ({
                ...prev,
                job: {
                    ...prev?.job,
                    urgent: false,
                } as Job,
            } as PaymentModel));
        }
        setIsUrgentSelected(!isUrgentSelected);        
    };

    const handleBumpUpOptiopnSelection = (option: AdPromotionType) => {
        setSelectedBumpUp(option);
        setPromoteAdData((prev) => ({
            ...prev,
            job: {
                ...prev?.job,
                bumpUp: true,
            } as Job,
        } as PaymentModel));
    }

    const handleUrgentOptiopnSelection = (option: AdPromotionType) => {
        setSelectedUrgent(option);
        setPromoteAdData((prev) => ({
            ...prev,
            job: {
                ...prev?.job,
                urgent: true,
            } as Job,
        } as PaymentModel));
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.name === "promoCode") {
            setPromoCode(event.target.value);
            if (event.target.value === "") {
                setPromoInvalid(false);
                setPromoInvalidMessage(undefined);
            }
        } else {
            setCheckout((prev) => ({ ...prev, [event.target.name]: event.target.value }));
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value); 
    }

    const validateInput = (name: string, value: string) => {
        if (name === "telephone") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Telephone number is required"}}));
            } 
            else if (!validateTelephoneNumber(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid telephone number!"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if (name === "tinNumber") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "TIN number is required"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if (name === "billingAddress") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Billing address is required"}}));
            } 
            else if (!validateAddress(value)) {
                setValidation((values: any) => ({ ...values, [name]: { valid: false, invalid: true, invalidMessage: "Invalid billing address!" } }));
            }
            else {
                setValidation((values: any) => ({ ...values, [name]: { valid: true, invalid: false }}));
            }
        }
    }

    const validateForm = (): number => {
        const formValidation: PaymentDetailsValidation = {
            telephone: { valid: true },
            tinNumber: { valid: true },
            billingAddress: { valid: true } 
        }
        if (!checkout.telephone || checkout.telephone === "") {
            formValidation.telephone = { valid: false, invalid: true, invalidMessage: "Telephone number is required" };
        } 
        else if (!validateTelephoneNumber(checkout.telephone)) {
            formValidation.telephone = { valid: false, invalid: true, invalidMessage: "Invalid telephone number!" };
        }
        if (!checkout.tinNumber || checkout.tinNumber === "") {
            formValidation.tinNumber = { valid: false, invalid: true, invalidMessage: "TIN number is required" };
        }
        if (!checkout.billingAddress || checkout.billingAddress === "") {
            formValidation.billingAddress = { valid: false, invalid: true, invalidMessage: "Billing address is required" };
        } 
        else if (!validateAddress(checkout.billingAddress)) {
            formValidation.billingAddress = { valid: false, invalid: true, invalidMessage: "Invalid billing address!" };
        }
        setValidation(formValidation);
        return Object.keys(formValidation).filter(key => (formValidation as any)[key].valid).length;
    }

    async function handleCheckoutClick() {
        let validInputCount = Object.keys(validation).filter(key => (validation as any)[key].valid).length;
        if (validInputCount < 3) {
            validInputCount = validateForm();
        }
        if (validInputCount === 3) {
            checkout.checkoutType = CheckoutTypes.AdPromotions;
            checkout.price = packagePrice;
            checkout.jobId = parseInt(jobId!);
            checkout.promoCode = promoCode;
            const promotionTypeIds: string[] = [];
            if (selectedBumpUp) {
                promotionTypeIds.push(selectedBumpUp.adPromotionTypeId.toString());
            }
            if (selectedUrgent) {
                promotionTypeIds.push(selectedUrgent.adPromotionTypeId.toString());
            }
            checkout.selectedPromotionTypes = promotionTypeIds.join(',');
            await postCheckoutRequest(checkout)
            .then((webXPayModel: WebXPayModel) => {
                $('.checkout').append(
                '<form action="' + webXPayModel.checkoutUrl + '" id="promotionForm" name="form" method="post" style="display:none;">' +
                    '<input type="text" name="first_name" value="' + webXPayModel.firstName + '" />' +
                    '<input type="text" name="last_name" value="' + webXPayModel.lastName + '" />' +
                    '<input type="text" name="email" value="' + webXPayModel.email + '" />' +
                    '<input type="text" name="contact_number" value="' + webXPayModel.contactNumber + '" />' +
                    '<input type="text" name="address_line_one" value="' + webXPayModel.address + '" />' +
                    '<input type="text" name="process_currency" value="' + webXPayModel.currency + '" />' +
                    '<input type="text" name="secret_key" value="' + webXPayModel.secretKey + '" />' +
                    '<input type="text" name="custom_fields" value="' + webXPayModel.customFields + '" />' +
                    '<input type="text" name="payment" value="' + webXPayModel.paymentStr + '" />' +
                '</form>');       
                var checkoutForm = document.getElementById("promotionForm") as HTMLFormElement;
                checkoutForm.submit();
            })
            .catch((error) => {
                toErrorPage(error);            
            });
        }
    }

    async function handlePromoCodeApply(event: React.FormEvent<HTMLFormElement>) { 
        event.stopPropagation();  
        await getPromoCodeValidation(recruiter!.recruiterId, promoCode, CheckoutTypes.AdPromotions, recruiter!.token)
        .then((response: PromoCode) => {
            if(response) {  
                setDiscountRate(response.discount);
                setPromoInvalid(false);
                setPromoInvalidMessage("");
            } 
            else {
                setDiscountRate(undefined);
                setPromoInvalid(true);
                setPromoInvalidMessage("Invalid Promo Code. Try Again");
            }
        })
        .catch((error) => {
            toErrorPage(error);            
        });    
    }

    async function getPromoteAdData() {
        setLoading(true);
        await getPromoteAdDetails(jobId!, recruiter!.recruiterId, recruiter!.token)
        .then((response: PaymentModel) => {
            setPromoteAdData(response);
            setCheckout((prev) => ({
                ...prev,
                telephone: response.organizationTelephone,
                tinNumber: response.organizationTINNumber,
                billingAddress: response.organizationAddress
            }));
            setLoading(false);
        })
        .catch((error) => {
            toErrorPage(error);            
        });
    }

    return (
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu>
                        <div style={styles.title}>{"Promote Advert"}</div>
                    </RecruiterMenu>
                </div>
                <div style={styles.rightWrapper}>
                    <div style={styles.topMenuWrapper}>
                        <div style={styles.backButton.container} onClick={() => navigate(-1)}>
                            <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                            <span style={styles.backButton.text}>{"Back"}</span>
                        </div>
                        <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                    </div>
                    {!isMobile && <div style={styles.title}>{"Promote Advert"}</div>}
                    <div style={styles.jobCardWrapper}>
                        {loading ? <PromoteAdLoader/> : <LongJobCard styles={styles.jobCardWrapper.card} job={promoteAdData!.job} isLastItem={true} hideApplySection={true} />}
                    </div>
                    {loading ? <ActivePromotionsLoader/> : promoteAdData && (promoteAdData.activeBumpUps > 0 || promoteAdData.activeUrgentJobs) && 
                    <div 
                        style={{
                            ...styles.activePromotionsWrapper, 
                            ...((promoteAdData.activeBumpUps > 0 && promoteAdData.activeUrgentJobs) && styles.activePromotionsWrapper.isActivePromotions)
                        }}
                    >
                        <div style={styles.activePromotionsWrapper.title}>{"Active Promotions"}</div>
                        <div style={styles.activePromotionsWrapper.activePromotions}>
                            {(promoteAdData.activeBumpUps > 0) && 
                            <div style={styles.activePromotionsWrapper.bumpUpWrapper}>
                                <div style={styles.activePromotionsWrapper.bumpUp}>
                                    <BumpUp style={styles.activePromotionsWrapper.bumpUpIcon}/>
                                </div>
                                <div style={styles.activePromotionsWrapper.remainingBumpUp}>{`Remaining Bump-Ups - ${promoteAdData.activeBumpUps}`}</div>
                            </div>}
                            {(promoteAdData.activeUrgentJobs != null) && 
                            <div style={styles.activePromotionsWrapper.urgentWrapper}>
                                <div style={styles.activePromotionsWrapper.urgent}>
                                    <Tag styles={styles.activePromotionsWrapper.urgentTag}>Urgent</Tag>
                                </div>
                                {isMobile ? (
                                    <div style={styles.activePromotionsWrapper.expireDateWrapper}>
                                        <div style={styles.activePromotionsWrapper.urgentBanner}>
                                            {"Urgent Banner"}
                                        </div>
                                        <div style={styles.activePromotionsWrapper.expireDate}>
                                            <span>{"Expire Date - "}</span>
                                            <span style={styles.activePromotionsWrapper.expireDate.date}>
                                                {new Date(promoteAdData.activeUrgentJobs.expireDate).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    ) : (
                                    <div style={styles.activePromotionsWrapper.expireDate}>
                                        {`Urgent Banner Expire Date - ${new Date(promoteAdData.activeUrgentJobs.expireDate).toLocaleDateString()}`}
                                    </div>
                                )}
                            </div>}
                        </div>
                    </div>}
                    <div style={styles.promoteAdContainer}>
                        <div style={{...styles.bumpUpContainer, ...(isBumpUpSelected && styles.bumpUpContainer.selected)}}>
                            <div style={styles.bumpUpWrapper}>
                                <div style={styles.bumpUpWrapper.checkBoxWrapper}>
                                    <CheckboxInput styles={styles.bumpUpWrapper.checkBox} value={isBumpUpSelected} handleChange={handleBumpUpSelection}/>
                                </div>
                                <div style={styles.bumpUpWrapper.bumpUp}>
                                    <BumpUp style={styles.bumpUpWrapper.bumpUpIcon}/>
                                </div>
                                <div style={styles.bumpUpWrapper.titleWrapper}>
                                    <div style={styles.bumpUpWrapper.title}>{"Daily Bump-Up"}</div>
                                    <div style={styles.bumpUpWrapper.description}>{"Get your advert to top of the list everyday to increase the views!"}</div>
                                </div>
                            </div>
                            {(promoteAdData && isBumpUpSelected) && <div style={styles.inputWrapper}>
                                {promoteAdData.adPromotionTypes.filter((option) => option.type === "BumpUp").map((option) => (
                                    <div style={styles.inputWrapper.options} onClick={() => handleBumpUpOptiopnSelection(option)}>
                                        <input
                                            type="radio"
                                            name="bumpUp"
                                            style={styles.inputWrapper.radioInput}
                                            value={option.value}
                                            checked={selectedBumpUp?.value === option.value}
                                        />
                                        <div style={styles.inputWrapper.daysCount}>{option.days} DAYS</div>
                                        <div style={styles.inputWrapper.price}>{option.price}</div>
                                    </div>
                                ))}
                            </div>}
                        </div>
                        <div style={{...styles.urgentContainer, ...(isUrgentSelected && styles.urgentContainer.selected)}}>
                            <div style={styles.urgentWrapper}>
                                <div style={styles.urgentWrapper.checkBoxWrapper}>
                                    <CheckboxInput styles={styles.urgentWrapper.checkBox} value={isUrgentSelected} handleChange={handleUrgentSelection}/>
                                </div>
                                <div style={styles.urgentWrapper.urgent}>
                                    <Tag styles={styles.urgentWrapper.urgentTag}>Urgent</Tag>
                                </div>
                                <div style={styles.urgentWrapper.titleWrapper}>
                                    <div style={styles.urgentWrapper.title}>{"Urgent"}</div>
                                    <div style={styles.urgentWrapper.description}>{"Highlight your advert from others to increase the views!"}</div>
                                </div>
                            </div>
                            {(promoteAdData && isUrgentSelected) && <div style={styles.inputWrapper}>
                                {promoteAdData.adPromotionTypes.filter((option) => option.type === "Urgent").map((option) => (
                                    <div style={styles.inputWrapper.options} onClick={() => handleUrgentOptiopnSelection(option)}>
                                        <input
                                            type="radio"
                                            name="Urgent"
                                            style={styles.inputWrapper.radioInput}
                                            value={option.value}
                                            checked={selectedUrgent?.value === option.value}
                                        />
                                        <div style={styles.inputWrapper.daysCount}>{option.days} DAYS</div>
                                        <div style={styles.inputWrapper.price}>{option.price}</div>
                                    </div>
                                ))}
                            </div>}
                        </div>
                    </div>
                    {(selectedBumpUp || selectedUrgent) && <div style={styles.checkout}>     
                        <div style={styles.checkout.priceSection}>
                            <div style={{...styles.checkout.promotionType, ...(styles.checkout.promotionType.bold)}}>
                                <div style={styles.checkout.promotionType.type}>{"AD PROMOTION TYPE"}</div>
                                <div style={styles.checkout.promotionType.price}>{"PRICE"}</div>
                            </div>
                            {selectedBumpUp && <div style={styles.checkout.promotionType}>
                                <div style={styles.checkout.promotionType.type}>{`Bump-Up - ${selectedBumpUp.days}`}</div>
                                <div style={styles.checkout.promotionType.price}>{`Rs. ${Number(selectedBumpUp.value).toFixed(2)}`}</div>
                            </div>}
                            {selectedUrgent && <div style={styles.checkout.promotionType}>
                                <div style={styles.checkout.promotionType.type}>{`Urgent - ${selectedUrgent?.days}`}</div>
                                <div style={styles.checkout.promotionType.price}>{`Rs. ${Number(selectedUrgent.value).toFixed(2)}`}</div>
                            </div>}
                            <div style={{...styles.checkout.promotionType, ...(styles.checkout.promotionType.bold)}}>
                                <div style={styles.checkout.promotionType.type}>{"Sub Total"}</div>
                                <div style={styles.checkout.promotionType.price}>{`Rs. ${Number(packagePrice).toFixed(2)}`}</div>
                            </div>
                            {(discount !== undefined && discount > 0) && <div style={styles.checkout.promotionType}>
                                <div style={styles.checkout.promotionType.type}>{"Discount"}</div>
                                <div style={styles.checkout.promotionType.price}>{`- Rs. ${Number(discount).toFixed(2)}`}</div>
                            </div>}
                            <div style={styles.checkout.promotionType}>
                                <div style={styles.checkout.promotionType.type}>{`VAT (${process.env.REACT_APP_VAT_RATE}%)`}</div>
                                <div style={styles.checkout.promotionType.price}>{`Rs. ${vatAmount.toFixed(2)}`}</div>
                            </div>
                            <div style={styles.checkout.promotionType}>
                                <div style={styles.checkout.promotionType.type}>{"Gross Total"}</div>
                                <div style={styles.checkout.promotionType.price}>{`Rs. ${price.toFixed(2)}`}</div>
                            </div>
                        </div>
                        <div style={{...styles.checkout.promoWrapper, ...(promoInvalid && (styles.checkout.promoInvalid))}}>   
                            <div style={styles.checkout.promoCode}>
                                <div style={{...styles.checkout.subTitle, ...styles.checkout.promoTitle}}>{"Have a Promo Code?"}</div>
                                <div style={styles.checkout.inputWrapper}>
                                    <TextInput
                                        inputType={'text'}
                                        name={'promoCode'}
                                        value={promoCode}
                                        handleChange={handleInputChange}
                                        styles={styles.checkout.inputCode}
                                        disabled={discount !== undefined && discount > 0}
                                        placeHolder={'XXXX XXXXX XXXXXXXXX'}
                                    />
                                    <Button 
                                        style={{...styles.checkout.button, ...(discountRate && styles.checkout.button.applied)}} 
                                        onClick={handlePromoCodeApply} 
                                        disabled={discountRate !== undefined}
                                    >
                                        {discountRate ? "APPLIED" : "APPLY"}
                                    </Button>
                                </div>
                            </div>
                            {promoInvalid && <div style={styles.checkout.promoInvalidMessage}>{promoInvalidMessage}</div>}
                        </div>
                        <div style={styles.inputContainer}>
                            <TextInput
                                inputType={'text'}
                                name={'telephone'}
                                value={checkout.telephone}
                                label={"Telephone Number *"}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                styles={styles.textInput}
                                validation={validation.telephone}
                            />
                            <TextInput
                                inputType={'text'}
                                name={'tinNumber'}
                                value={checkout.tinNumber}
                                label={'TIN Number *'}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                styles={styles.textInput}
                                validation={validation.tinNumber}
                            />
                            <TextInput
                                inputType={'text'}
                                name={'billingAddress'}
                                value={checkout.billingAddress}
                                label={'Address *'}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                styles={styles.textInput}
                                validation={validation.billingAddress}
                            />
                        </div>  
                        <Button type="button" style={styles.checkout.checkoutButton} onClick={() => handleCheckoutClick()}>{"PROCEED TO CHECKOUT"}</Button> 
                        <div style={styles.checkout.iconGride}>
                            {PaymentIcons.map((paymentIcon, index) =>                    
                                <img key={index} src={paymentIcon} style={styles.checkout.paymentIcon}/>                    
                            )}
                        </div> 
                        <p style={styles.checkout.note}>{"Kindly note that we're unable to process any refunds."}</p>              
                    </div>}
                </div>
            </div>
            <div className="checkout"></div>            
            <Footer/>
        </>
    )
}
